import { reasonFromStatus } from './choose.ts';
import { publishableKey } from './km-3d.ts';

/**
 * `<km-3d-button sku key [ar] [label] [api] [no-check]>` — dugme „3D pregled" koje prodavnica
 * postavi preko slike proizvoda; klik otvara modal sa `<km-3d>`. Dok niko ne klikne, stranica
 * ne učitava ni model-viewer ni model (samo mali resolve da se dugme ne pokaže bez modela).
 *
 * Dijalog živi u shadow root-u dugmeta: CSS prodavnice ga ne dira, a `showModal()` ga ionako
 * diže u top layer iznad svega na strani.
 */
const STYLE = `
:host{display:inline-block}
:host([hidden]),:host([data-state=unavailable]),:host([data-state=checking]){display:none!important}
.open{display:inline-flex;align-items:center;gap:8px;font:600 14px/1 system-ui,-apple-system,sans-serif;
padding:9px 14px;border:0;border-radius:var(--km3d-button-radius,8px);cursor:pointer;
background:var(--km3d-button-bg,rgba(15,23,42,.88));color:var(--km3d-button-color,#fff);
box-shadow:0 1px 3px rgba(0,0,0,.2)}
.open:hover{filter:brightness(1.15)}
.open:focus-visible,.close:focus-visible{outline:2px solid #2563eb;outline-offset:2px}
.open svg{width:16px;height:16px;flex:none}
dialog{padding:0;border:0;border-radius:12px;background:#fff;max-width:none;
width:min(92vw,820px,calc(100vh - 140px));width:min(92vw,820px,calc(100dvh - 140px));
max-height:none;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.35)}
dialog::backdrop{background:rgba(15,23,42,.6)}
.head{display:flex;align-items:center;gap:12px;padding:10px 10px 10px 16px;
font:600 15px/1.3 system-ui,-apple-system,sans-serif;color:#0f172a}
.title{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.close{flex:none;width:40px;height:40px;border:0;border-radius:999px;background:#f1f5f9;color:#0f172a;
font:400 24px/1 system-ui,sans-serif;cursor:pointer}
km-3d{--km3d-aspect:1/1;width:100%}
.note{margin:0;padding:8px 16px 0;font:12.5px/1.4 system-ui,-apple-system,sans-serif;color:#64748b;text-align:center}
.note[hidden]{display:none}
.note+.hint{padding-top:2px}
.hint{margin:0;padding:8px 16px 14px;font:13px/1.4 system-ui,-apple-system,sans-serif;color:#64748b;text-align:center}
@media (max-width:640px){
  dialog{width:100vw;height:100dvh;border-radius:0;display:flex;flex-direction:column}
  dialog:not([open]){display:none}
  km-3d{--km3d-aspect:auto;flex:1}
}
`;

const CUBE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7 12 12l8.7-5M12 22V12"/></svg>';

export class Km3dButtonElement extends HTMLElement {
  static observedAttributes = ['sku', 'key', 'publishable-key', 'label'];

  #root: ShadowRoot;
  #btn: HTMLButtonElement;
  #dialog: HTMLDialogElement | null = null;
  #checked: string | null = null;
  #name = '';
  /** Napomena uz model iz resolve-a (npr. „Kabina je univerzalna…"); prazno = bez reda. */
  #note = '';
  #savedOverflow: string | null = null;

  constructor() {
    super();
    this.#root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = STYLE;
    this.#btn = document.createElement('button');
    this.#btn.type = 'button';
    this.#btn.className = 'open';
    this.#btn.setAttribute('part', 'button');
    this.#btn.addEventListener('click', () => this.open());
    this.#root.append(style, this.#btn);
    this.#label();
  }

  #label(): void {
    this.#btn.innerHTML = CUBE;
    this.#btn.append(this.getAttribute('label') ?? '3D pregled');
  }

  get #api(): string {
    const own = new URL(import.meta.url).origin;
    return (this.getAttribute('api') ?? own).replace(/\/$/, '');
  }

  connectedCallback(): void {
    void this.#check();
  }

  attributeChangedCallback(name: string, old: string | null, value: string | null): void {
    if (name === 'label') this.#label();
    if (name === 'sku' && old !== null && old !== value) void this.#check();
    // Ključ ume da stigne posle povezivanja (React ga postavlja kroz ref/efekat).
    if ((name === 'key' || name === 'publishable-key') && value && old !== value) void this.#check();
  }

  /** Dugme se pokazuje tek kad API potvrdi da model postoji (osim sa `no-check`). */
  async #check(): Promise<void> {
    const sku = this.getAttribute('sku');
    const key = publishableKey(this);
    if (!sku || !key) {
      this.setAttribute('data-state', 'unavailable');
      return;
    }
    if (this.hasAttribute('no-check')) {
      this.setAttribute('data-state', 'ready');
      return;
    }
    if (this.#checked === sku) return;
    this.#checked = sku;
    this.setAttribute('data-state', 'checking');
    try {
      const res = await fetch(
        `${this.#api}/v1/resolve?sku=${encodeURIComponent(sku)}&key=${encodeURIComponent(key)}`,
        { credentials: 'omit' },
      );
      if (this.getAttribute('sku') !== sku) return; // u međuvremenu promenjena varijanta
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        this.#unavailable(reasonFromStatus(res.status, body.error));
        return;
      }
      const body = (await res.json()) as { name?: string; note?: string };
      this.#name = body.name ?? '';
      this.#note = body.note ?? '';
      this.setAttribute('data-state', 'ready');
    } catch {
      this.#unavailable('network');
    }
  }

  #unavailable(reason: string): void {
    this.setAttribute('data-state', 'unavailable');
    this.dispatchEvent(
      new CustomEvent('km3d:unavailable', {
        bubbles: true,
        composed: true,
        detail: { sku: this.getAttribute('sku'), reason },
      }),
    );
  }

  /** Otvara modal sa 3D prikazom (može i iz koda prodavnice). */
  open(): void {
    const sku = this.getAttribute('sku');
    const key = publishableKey(this);
    if (!sku || !key) return;
    if (!this.#dialog) this.#dialog = this.#buildDialog();
    const viewer = this.#dialog.querySelector('km-3d');
    if (viewer && viewer.getAttribute('sku') !== sku) viewer.setAttribute('sku', sku);
    const title = this.#dialog.querySelector('.title');
    // Naziv iz prodavnice (kako ga kupac vidi na strani) ima prednost nad nazivom iz kataloga.
    if (title) title.textContent = this.getAttribute('title') || this.#name || '3D pregled';
    this.#showNote();
    this.#dialog.showModal();
    if (this.#savedOverflow === null) {
      this.#savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
  }

  /** Tekst ide kao textContent (bez HTML-a); red se skriva kad napomene nema. */
  #showNote(): void {
    const note = this.#dialog?.querySelector<HTMLElement>('.note');
    if (!note) return;
    note.textContent = this.#note;
    note.hidden = !this.#note;
  }

  close(): void {
    this.#dialog?.close();
    this.#unlockScroll();
  }

  /** Vraća skrol stranice kakav je bio pre otvaranja (ne briše tuđi inline stil). */
  #unlockScroll(): void {
    if (this.#savedOverflow === null) return;
    document.documentElement.style.overflow = this.#savedOverflow;
    this.#savedOverflow = null;
  }

  #buildDialog(): HTMLDialogElement {
    const d = document.createElement('dialog');
    d.setAttribute('part', 'dialog');
    d.setAttribute('aria-label', '3D pregled');
    const head = document.createElement('div');
    head.className = 'head';
    const title = document.createElement('span');
    title.className = 'title';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'close';
    close.setAttribute('aria-label', 'Zatvori');
    close.textContent = '×';
    // showModal fokusira prvi fokusabilni element; bez ovoga je to model-viewer (okvir fokusa oko modela).
    close.autofocus = true;
    close.addEventListener('click', () => this.close());
    head.append(title, close);

    const viewer = document.createElement('km-3d');
    for (const a of [
      'sku',
      'key',
      'publishable-key',
      'api',
      'camera-orbit',
      'material-variant',
      'variant',
      'dimensions',
    ]) {
      const v = this.getAttribute(a);
      if (v !== null) viewer.setAttribute(a, v);
    }
    if (this.hasAttribute('ar')) viewer.setAttribute('ar', '');
    viewer.setAttribute('loading', 'eager');

    // Sa `no-check` (ili posle promene SKU-a) napomena stiže iz resolve-a samog viewer-a.
    viewer.addEventListener('km3d:ready', (e) => {
      this.#note = (e as CustomEvent<{ note?: string | null }>).detail.note ?? '';
      this.#showNote();
    });

    const note = document.createElement('p');
    note.className = 'note';
    note.setAttribute('part', 'note');
    note.hidden = true;

    const hint = document.createElement('p');
    hint.className = 'hint';
    hint.textContent = matchMedia('(pointer: coarse)').matches
      ? 'Prevucite prstom da okrenete · dva prsta za zum'
      : 'Prevucite mišem da okrenete · točkić za zum';
    d.append(head, viewer, note, hint);
    // Esc: `cancel` stiže sinhrono; `close` event browser šalje tek kasnije (ne oslanjamo se na njega).
    d.addEventListener('cancel', () => this.#unlockScroll());
    d.addEventListener('close', () => this.#unlockScroll());
    // Klik na zatamnjenu pozadinu (van okvira) zatvara.
    d.addEventListener('click', (e) => {
      if (e.target !== d) return;
      const r = d.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) this.close();
    });
    this.#root.append(d);
    return d;
  }
}
